import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { TbX } from 'react-icons/tb';
import { ImSpinner2 } from 'react-icons/im';
import { IconType } from 'react-icons';
import clsx from 'clsx';

import useClickOutside from '@/@core/hooks/useClickOutside';
import Error from './atoms/ErrorMessage/ErrorMessage';
import { Input } from './Input';

const openSelects = new Set<string>();
const selectInstances = new Map<string, () => void>();

const closeOtherSelects = (currentId: string) => {
  openSelects.forEach((id) => {
    if (id !== currentId && selectInstances.has(id)) {
      selectInstances.get(id)?.();
    }
  });
  openSelects.clear();
  openSelects.add(currentId);
};

interface Props<K> {
  className?: string;
  children: (item: K, index?: number) => React.ReactNode;
  options: K[];
  onSelected: (item: K) => void;
  onDeselected?: () => void;
  value?: string;
  id?: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  IconStart?: IconType;
  IconEnd?: IconType;
  error?: string | string[] | null;
  validated?: boolean;
  loading?: boolean;
  automaticSelection?: boolean;
  maxLength?: number;
  compareFn?: (item: K) => boolean;
}

interface OptionProps<K> {
  className?: string;
  children: React.ReactNode;
  data: K;
}

interface TSelectSearchContext {
  open: boolean;
  toggle: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectedClick: (value: any) => void;
  automaticSelection?: boolean;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
}

const SelectSearchContext = createContext<TSelectSearchContext>({} as TSelectSearchContext);

export function SelectAsync<K>({
  children,
  className,
  options,
  onSelected,
  onDeselected,
  placeholder = 'Search',
  IconStart,
  IconEnd,
  error,
  loading = false,
  automaticSelection = false,
  compareFn,
  validated = false,
  id,
  ...props
}: Props<K>) {
  const [open, setOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const optionsListRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef<boolean>(false);
  const isFocusedRef = useRef<boolean>(false);
  const justSelectedRef = useRef<boolean>(false);
  const lastSelectionTimeRef = useRef<number>(0);
  const selectId = useRef<string>(id || `select-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    const closeFn = () => {
      setOpen(false);
      setHighlightedIndex(-1);
      openSelects.delete(selectId.current);
    };
    selectInstances.set(selectId.current, closeFn);
    return () => {
      selectInstances.delete(selectId.current);
      openSelects.delete(selectId.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      openSelects.add(selectId.current);
    } else {
      openSelects.delete(selectId.current);
    }
  }, [open]);

  useClickOutside(inputRef, () => {
    if (open && !isNavigatingRef.current) {
      setOpen(false);
      setHighlightedIndex(-1);
      openSelects.delete(selectId.current);
    }
  });

  const renderOption = (item: K, index: number) => children(item, index);

  const handleSelectedClick = (item: K) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    justSelectedRef.current = true;
    lastSelectionTimeRef.current = Date.now();
    onSelected(item);
    setOpen(false);
    setHighlightedIndex(-1);
    openSelects.delete(selectId.current);
    setTimeout(() => {
      inputElementRef.current?.focus();
      setTimeout(() => {
        justSelectedRef.current = false;
      }, 500);
    }, 0);
  };

  const resetState = () => {
    setOpen(false);
    setHighlightedIndex(-1);
    openSelects.delete(selectId.current);
    if (onDeselected) {
      onDeselected();
    }
  };

  useEffect(() => {
    const timeSinceSelection = Date.now() - lastSelectionTimeRef.current;

    if (automaticSelection && options.length === 1) {
      if (justSelectedRef.current || timeSinceSelection < 500) {
        return;
      }
      justSelectedRef.current = true;
      lastSelectionTimeRef.current = Date.now();
      onSelected(options[0]);
      setTimeout(() => {
        setOpen(false);
        setHighlightedIndex(-1);
        openSelects.delete(selectId.current);
        justSelectedRef.current = false;
      }, 100);
      return;
    }

    if (!isFocusedRef.current || justSelectedRef.current || open || timeSinceSelection < 500) {
      return;
    }

    if (automaticSelection) {
      if (options.length > 0) {
        closeOtherSelects(selectId.current);
        setOpen(true);
        setHighlightedIndex(-1);
      }
    } else if (!automaticSelection) {
      if (options.length > 0) {
        closeOtherSelects(selectId.current);
        setOpen(true);
        setHighlightedIndex(-1);
      }
    }
  }, [automaticSelection, options.length]);

  useEffect(() => {
    if (highlightedIndex >= options.length) {
      setHighlightedIndex(-1);
    }
  }, [options.length, highlightedIndex]);

  useEffect(() => {
    if (open && highlightedIndex >= 0 && optionsListRef.current) {
      const optionElements = optionsListRef.current.querySelectorAll('button');
      const highlightedElement = optionElements[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex, open]);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (inputRef.current?.contains(relatedTarget)) {
      return;
    }

    isFocusedRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (automaticSelection) {
        if (compareFn && props.value && options.some((option) => compareFn(option))) {
          return;
        }
        if (!loading) {
          setTimeout(() => {
            onDeselected?.();
          }, 100);
        }
      }
      setOpen(false);
      setHighlightedIndex(-1);
      openSelects.delete(selectId.current);
    }, 150);
  };

  const onFocus = () => {
    isFocusedRef.current = true;
    if (justSelectedRef.current) {
      return;
    }
    closeOtherSelects(selectId.current);

    if (automaticSelection) {
      if (options.length > 1) {
        setOpen(true);
        setHighlightedIndex(-1);
      }
    } else {
      if (options.length > 0) {
        setOpen(true);
        setHighlightedIndex(-1);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) {
      if (e.key === 'ArrowDown' && options.length > 0) {
        e.preventDefault();
        closeOtherSelects(selectId.current);
        setOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        isNavigatingRef.current = true;
        setHighlightedIndex((prev) => {
          const next = prev < options.length - 1 ? prev + 1 : 0;
          return next;
        });
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 0);
        break;

      case 'ArrowUp':
        e.preventDefault();
        isNavigatingRef.current = true;
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : options.length - 1;
          return next;
        });
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 0);
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleSelectedClick(options[highlightedIndex]);
        } else if (options.length === 1) {
          handleSelectedClick(options[0]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        openSelects.delete(selectId.current);
        inputElementRef.current?.blur();
        break;

      case 'Tab':
        setOpen(false);
        setHighlightedIndex(-1);
        openSelects.delete(selectId.current);
        break;

      default:
        if (highlightedIndex >= 0) {
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  return (
    <div>
      <div className="relative z-10" ref={inputRef}>
        <SelectSearchContext.Provider
          value={{
            open,
            toggle: setOpen,
            handleSelectedClick,
            automaticSelection,
            highlightedIndex,
            setHighlightedIndex,
          }}
        >
          <div
            className={clsx(
              'group/item relative rounded border border-gray-300 focus-within:border-blue-400',
              { 'border-red-500': !!error && !validated },
              { 'border-emerald-500': validated },
            )}
          >
            <Input
              ref={inputElementRef}
              className={clsx('w-full rounded border-none', className)}
              placeholder={placeholder}
              IconStart={IconStart}
              autoComplete="off"
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={handleKeyDown}
              IconEnd={IconEnd}
              readOnly={loading}
              {...props}
            />
            <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center px-0.5 text-gray-400">
              <TbX
                className={clsx(
                  'pointer-events-auto invisible relative cursor-pointer select-none text-sm text-red-600',
                  !!props.value?.length && 'group-hover/item:visible',
                  loading && '!pointer-events-none !invisible',
                )}
                onClick={!loading ? resetState : undefined}
              />
              {loading && <ImSpinner2 className="animate-spin" size={14} />}
            </div>
          </div>
          {open && !!options.length && (
            <div
              ref={optionsListRef}
              className="scroll__styled absolute top-full z-20 flex max-h-[250px] w-full flex-col overflow-y-auto overflow-x-hidden rounded border bg-white dark:border-slate-700 dark:bg-slate-800"
              role="listbox"
            >
              {options.map((item, index) => (
                <div key={index} data-option-index={index}>
                  {renderOption(item, index)}
                </div>
              ))}
            </div>
          )}
        </SelectSearchContext.Provider>
      </div>
      <Error message={error} />
    </div>
  );
}

export function Option<K>({ data, children, className, ...props }: OptionProps<K>) {
  const { handleSelectedClick, highlightedIndex, setHighlightedIndex } =
    useContext(SelectSearchContext);
  const optionRef = useRef<HTMLButtonElement | null>(null);
  const [index, setIndex] = useState<number>(-1);

  useEffect(() => {
    if (optionRef.current?.parentElement) {
      const wrapper = optionRef.current.parentElement;
      const dataIndex = wrapper.getAttribute('data-option-index');
      if (dataIndex !== null) {
        setIndex(parseInt(dataIndex, 10));
      } else {
        const listContainer = wrapper.parentElement;
        if (listContainer) {
          const options = Array.from(listContainer.children);
          setIndex(options.indexOf(wrapper));
        }
      }
    }
  }, []);

  const isHighlighted = index >= 0 && index === highlightedIndex;

  return (
    <button
      ref={optionRef}
      role="option"
      type="button"
      aria-selected={isHighlighted}
      className={clsx(
        'w-full flex-1 p-1 text-start text-xs transition-colors',
        'dark:text-slate-300 dark:ring-gray-700',
        isHighlighted
          ? 'bg-blue-100 dark:bg-slate-700'
          : 'bg-white hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700',
        'focus:bg-blue-100 focus:outline-none dark:focus:bg-slate-700',
        className,
      )}
      onClick={() => handleSelectedClick(data)}
      onMouseEnter={() => {
        if (index >= 0) {
          setHighlightedIndex(index);
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}

SelectAsync.Option = Option;
